import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Tab, Tabs } from '@mui/material'
import { Person, FormatListNumbered, Settings } from '@mui/icons-material'

import { TournamentIcon } from 'assets/icons'
import UserEditForm from 'components/form/user/UserEditForm'
import UserGames from 'components/user/UserGames'
import UserChallenges from 'components/user/UserChallenges'
import { selectUser, clearUser } from 'store/user/slice'
import { ClientRoute } from 'routes/client'
import PageHeader from 'components/user/PageHeader'
import { getUser, getUserStats, getUserExtra, getUserGameStats, getUserLinkedAccounts } from 'store/user/thunks'

interface ParamTypes extends ParamTypesBase {
  id: string
  page?: USER_PAGES
}

enum USER_PAGES {
  Profile = 'profile',
  Games = 'games',
  Challenges = 'challenges',
  Statistics = 'statistics',
  Settings = 'settings',
}

export default function User(): JSX.Element {
  const { id: paramId, page } = useParams<ParamTypes>()
  const id = paramId || ''
  const [currentId, setCurrentId] = useState<string>('')
  const initialPage = page && Object.values(USER_PAGES).includes(page) ? page : USER_PAGES.Games
  const [currentTab, setCurrentTab] = useState<string>(initialPage)
  const [isProfileFormDirty, setIsProfileFormDirty] = useState<boolean>(false)
  const intl = useIntl()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userState = useSelector(selectUser)

  useEffect(() => {
    if ((id && status === 'idle') || currentId !== id) {
      setCurrentId(id)
      dispatch(getUser(id))
      dispatch(getUserStats(parseInt(id)))
      dispatch(getUserExtra(parseInt(id)))
      dispatch(getUserGameStats(parseInt(id)))
      dispatch(getUserLinkedAccounts(parseInt(id)))
    }
  }, [id, currentId, status, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearUser())
    }
  }, [dispatch])

  useEffect(() => {
    if ((id && status === 'idle') || currentId !== id) {
      setCurrentId(id)
    }
  }, [id, currentId, status, dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearUser())
    }
  }, [dispatch])

  const handleChange = (event, tabValue) => {
    const shouldAskConfirmation = isProfileFormDirty && currentTab === USER_PAGES.Profile
    const confirmMessage = intl.formatMessage({ id: 'strings.leavePageConfirmation' })
    const newUrl = ClientRoute.ADMIN_USER(parseInt(id), tabValue)
    if (shouldAskConfirmation && window.confirm(confirmMessage)) {
      setCurrentTab(tabValue)
      navigate(newUrl)
    } else if (!shouldAskConfirmation) {
      setCurrentTab(tabValue)
      navigate(newUrl)
    }
  }

  const profileFormStatus = (isDirty: boolean) => {
    setIsProfileFormDirty(isDirty)
  }

  const getChallengeIcon = () => {
    return currentTab === USER_PAGES.Challenges ? <ActiveChallengeIcon /> : <ChallengeIcon />
  }

  return (
    <PageContainer>
      <PageHeader userInfo={userState} />
      <TabsContainer value={currentTab} onChange={handleChange}>
        <Tab value={USER_PAGES.Profile} icon={<Person />} label={intl.formatMessage({ id: 'strings.profile' })} />
        <Tab
          value={USER_PAGES.Games}
          icon={<FormatListNumbered />}
          label={intl.formatMessage({ id: 'strings.games' })}
        />
        <Tab
          value={USER_PAGES.Challenges}
          icon={getChallengeIcon()}
          label={intl.formatMessage({ id: 'strings.challenges' })}
        />
        <Tab
          value={USER_PAGES.Statistics}
          icon={<Settings />}
          label={intl.formatMessage({ id: 'strings.statistics' })}
          disabled
        />
        <Tab
          value={USER_PAGES.Settings}
          icon={<Settings />}
          label={intl.formatMessage({ id: 'strings.settings' })}
          disabled
        />
      </TabsContainer>
      <ContentContainer>
        {currentTab === USER_PAGES.Profile && <UserEditForm userState={userState} formStatus={profileFormStatus} />}
        {currentTab === USER_PAGES.Games && <UserGames userState={userState} />}
        {currentTab === USER_PAGES.Challenges && <UserChallenges userState={userState} />}
        {currentTab === USER_PAGES.Statistics && <span>stats</span>}
        {currentTab === USER_PAGES.Settings && <span>settings</span>}
      </ContentContainer>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  margin: 10px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: calc(100vw - 110px);
  padding-top: 16px;
`

const TabsContainer = styled(Tabs)`
  margin: 10px;
  background-color: ${(props) => props.theme.customPalette.light};
  width: 100%;

  & .MuiTabs-indicator {
    height: 0;
  }

  & button {
    padding: 10px;
    border-right: 2px solid;
    border-color: ${(props) => props.theme.customPalette.lightGray};
    width: 160px;

    &.Mui-selected {
      color: ${(props) => props.theme.palette.primary.main};
      background-color: #fafafa;
    }
  }
`

const ContentContainer = styled.div`
  padding: 10px 0 20px 20px;
  min-height: 500px;
  background-color: ${(props) => props.theme.customPalette.light};
`

const ChallengeIcon = styled(TournamentIcon)`
  width: 24px;
  height: 24px;
  fill: ${(props) => props.theme.customPalette.darkGray};
`

const ActiveChallengeIcon = styled(ChallengeIcon)`
  fill: ${(props) => props.theme.palette.primary.main};
`
